/**
 * Wall Builder Calculator Utilities
 *
 * This module provides comprehensive utilities for calculating wall construction materials,
 * including blocks, corner stones, top stones, and end stones. It supports multiple wall types
 * with different height limitations and provides visual preview capabilities.
 *
 * @fileoverview Wall building calculation engine with visual preview support
 * @author Loucris Pongasi
 * @version 1.0.0
 */
import { createDraft, finishDraft } from 'immer';
import { LoadProductDataItems } from '@themes/common/data';
/**
 * Calculates the total number of blocks and special stones needed for wall construction
 *
 * This function performs comprehensive calculations for all stone types:
 * - Regular blocks: Main structural elements
 * - Corner stones: Connect segments at angles (not available for last segment)
 * - Top stones: Cap the top of wall segments
 * - End stones: Provide finished ends (starting stones use same product as end stones)
 *
 * @param segments - Array of wall segment configurations
 * @param block - Selected block type with dimensions and pricing
 * @param baseSized - Scale factor for visual preview (100 = actual size)
 * @returns Complete calculation results including costs and visual preview data
 *
 * @example
 * ```typescript
 * const segments = [
 *   { id: '1', height: 2000, length: 4000, topStone: true, cornerStones: 1 }
 * ];
 * const block = defaultWallBlocks[0]; // Stenbolaget 140×420
 * const result = calculateWallBlocks(segments, block, 100);
 * console.log(`Total blocks needed: ${result.totalBlocks}`);
 * console.log(`Total cost: ${FormatMoney(result.totalCost * 100)}`);
 * ```
 */
export const calculateWallBlocks = (state) => {
    var _a, _b, _c, _d;
    const { segments, selectedBlock, zoom, selectedTopCornerStoneId, selectedEndStoneId, selectedTopStoneId } = state;
    const block = selectedBlock; // Ensure block is selected
    // Filter out segments with empty height or length values
    const validSegments = segments.filter((segment) => typeof segment.height === 'number' && segment.height > 0 && typeof segment.length === 'number' && segment.length > 0);
    if (validSegments.length === 0 || !selectedBlock) {
        return {
            segments: [],
            block,
            totalBlocks: 0,
            cornerStones: 0,
            topStones: 0,
            endStones: 0,
            topCornerStones: 0,
            totalCost: 0,
            cornerStoneCost: 0,
            topStoneCost: 0,
            endStoneCost: 0,
            topCornerStoneCost: 0,
            selectedTopStone: null,
            selectedEndStone: null,
            selectedTopCornerStone: null,
            extraProducts: [],
        };
    }
    // Get selected stones for calculations
    // Initialize selectedTopStone based on user selection or default
    let selectedTopStoneWrapper = null;
    let selectedTopStone = null;
    if (selectedTopStoneId === '') {
        // User explicitly selected "none" - no top stone
        selectedTopStoneWrapper = null;
        selectedTopStone = null;
    }
    else if (selectedTopStoneId && selectedTopStoneId !== '') {
        // User selected a specific top stone
        const selectedStone = block.top_stones.find((stone) => { var _a; return ((_a = stone.variant.variant_id) === null || _a === void 0 ? void 0 : _a.toString()) === selectedTopStoneId; }) || null;
        if (selectedStone) {
            selectedTopStoneWrapper = selectedStone;
            selectedTopStone = selectedTopStoneWrapper.variant;
        }
    }
    else {
        // No user selection - use default if available
        selectedTopStoneWrapper = block.default_top_stone || null;
        selectedTopStone = (selectedTopStoneWrapper === null || selectedTopStoneWrapper === void 0 ? void 0 : selectedTopStoneWrapper.variant) || null;
    }
    const selectedEndStone = block.end_stones.find((stone) => { var _a; return ((_a = stone.variant_id) === null || _a === void 0 ? void 0 : _a.toString()) === selectedEndStoneId; }) || null;
    const selectedTopCornerStone = block.corner_top && ((_a = block.corner_top.variant.variant_id) === null || _a === void 0 ? void 0 : _a.toString()) === selectedTopCornerStoneId ? block.corner_top : null;
    let totalBlocks = 0;
    // block size will scale with base size
    let blockHeight = Math.ceil(block.main_variant.thickness * (zoom / 100)); // width represents height for wall blocks
    let blockLength = Math.ceil(block.main_variant.length * (zoom / 100));
    // Adjust block dimensions based on base size base on segments height and length
    const segmentDraft = createDraft(validSegments.map((segment, segmentIndex) => {
        // Calculate vertical blocks considering topstone replacement
        let totalBlocksVertical;
        let effectiveHeight;
        let effectiveHeightWithoutMortar;
        const segmentHeight = segment.height; // Safe since we filtered valid segments
        const segmentLength = segment.length; // Safe since we filtered valid segments
        // Apply +10% only if wall requires 2 or more rows; otherwise exact height
        // We'll first compute an initial estimate of rows to decide whether to apply the extra allowance
        const baseThickness = block.main_variant.thickness;
        const tentativeRows = Math.ceil(segmentHeight / baseThickness);
        const applyExtra = tentativeRows >= 2;
        const heightWithMortar = applyExtra ? Math.round(segmentHeight * 1.1 * 1000) / 1000 : segmentHeight;
        if (selectedTopStone && selectedTopStone) {
            // If topstone is selected, we need to ensure the total height meets the target
            // The top stone has a different height than regular blocks, so we calculate:
            // total height = (number of regular blocks * regular block height) + top stone height
            // This must be >= heightWithMortar (target height + mortar allowance)
            // Start by calculating how many regular blocks we need
            let regularBlocksNeeded = Math.ceil((heightWithMortar - selectedTopStone.thickness) / baseThickness);
            // Ensure we have at least 0 regular blocks (can't be negative)
            regularBlocksNeeded = Math.max(0, regularBlocksNeeded);
            // Calculate the effective height with the regular blocks + top stone
            let calculatedHeight = regularBlocksNeeded * baseThickness + selectedTopStone.thickness;
            // If the calculated height is less than required, add more regular blocks
            while (calculatedHeight < heightWithMortar && regularBlocksNeeded >= 0) {
                regularBlocksNeeded += 1;
                calculatedHeight = regularBlocksNeeded * baseThickness + selectedTopStone.thickness;
            }
            totalBlocksVertical = regularBlocksNeeded;
            effectiveHeight = calculatedHeight;
            // Without mortar, target original segmentHeight (no 10% increase)
            if (selectedTopStone && selectedTopStone) {
                let regularBlocksNoMortar = Math.ceil((segmentHeight - selectedTopStone.thickness) / baseThickness);
                regularBlocksNoMortar = Math.max(0, regularBlocksNoMortar);
                let calculatedNoMortar = regularBlocksNoMortar * baseThickness + selectedTopStone.thickness;
                while (calculatedNoMortar < segmentHeight && regularBlocksNoMortar >= 0) {
                    regularBlocksNoMortar += 1;
                    calculatedNoMortar = regularBlocksNoMortar * baseThickness + selectedTopStone.thickness;
                }
                effectiveHeightWithoutMortar = calculatedNoMortar;
            }
            else {
                // Fallback if no top stone
                effectiveHeightWithoutMortar = Math.ceil(segmentHeight / baseThickness) * baseThickness;
            }
        }
        else {
            // No topstone, use regular calculation
            totalBlocksVertical = Math.ceil(heightWithMortar / baseThickness);
            // If only one row, ensure no extra added due to rounding mistake
            if (!applyExtra && totalBlocksVertical === 2 && segmentHeight <= baseThickness) {
                totalBlocksVertical = 1;
            }
            effectiveHeight = totalBlocksVertical * baseThickness;
            // Without mortar uses raw segmentHeight as target
            effectiveHeightWithoutMortar = Math.ceil(segmentHeight / baseThickness) * baseThickness;
        }
        // Calculate horizontal blocks considering endstones, corner stones, and top stones
        let totalBlocksHorizontal;
        let topStonesHorizontal;
        let effectiveLength;
        // Calculate corner stone length contribution
        let cornerStoneLength = 0;
        const isCornerSegment = segmentIndex < validSegments.length - 1 && segment.cornerStones > 0 && block.corner_stone;
        if (isCornerSegment) {
            cornerStoneLength = block.corner_stone.length;
        }
        // Calculate top stone horizontal blocks if top stones are selected
        // This is separate from main segment length calculation
        if (selectedTopStone && selectedTopStone) {
            // Top stones have their own length, calculate how many fit in the segment
            // But this shouldn't affect the main segment length calculation
            topStonesHorizontal = Math.ceil(segmentLength / selectedTopStone.length);
            // Ensure we have at least 1 top stone if selected
            if (topStonesHorizontal === 0) {
                topStonesHorizontal = 1;
            }
        }
        else {
            topStonesHorizontal = 0;
        }
        if ((segment.startingStone || segment.endStone) && selectedEndStone) {
            // Calculate blocks per row: FIRST account for special stones (corner, end, starting) as individual stones,
            // THEN calculate remaining main stones based on specified length
            let specialStonesLength = 0;
            let endStoneLength = 0;
            // FIRST: Account for special stones as individual stones that take priority
            if (segment.startingStone) {
                endStoneLength += selectedEndStone.length;
                specialStonesLength += selectedEndStone.length;
            }
            if (segment.endStone) {
                endStoneLength += selectedEndStone.length;
                specialStonesLength += selectedEndStone.length;
            }
            // Always include corner stone if selected and available
            if (isCornerSegment) {
                specialStonesLength += cornerStoneLength;
            }
            // SECOND: Calculate remaining length for main blocks after accounting for special stones
            const remainingLength = Math.max(0, segmentLength - specialStonesLength);
            const mainBlocksHorizontal = Math.ceil(remainingLength / block.main_variant.length);
            // Ensure we have at least 1 main block if the remaining length requires it
            const finalMainBlocksHorizontal = mainBlocksHorizontal === 0 && remainingLength > 0 ? 1 : mainBlocksHorizontal;
            // Count special stones for total blocks (1 per selected special stone)
            let specialStonesCount = 0;
            if (segment.startingStone)
                specialStonesCount += 1;
            if (segment.endStone)
                specialStonesCount += 1;
            if (isCornerSegment)
                specialStonesCount += 1;
            // Total horizontal blocks includes main blocks + special stones
            // Always use main blocks count for the segment structure, regardless of top stone selection
            totalBlocksHorizontal = finalMainBlocksHorizontal + specialStonesCount;
            // Calculate actual effective length based on main block dimensions + special stones
            // Top stone selection should not affect the segment length
            effectiveLength = finalMainBlocksHorizontal * block.main_variant.length + specialStonesLength;
            // Ensure effective length meets minimum requirement
            if (effectiveLength < segmentLength) {
                // Add more main blocks if needed to meet segment length
                // Top stone selection should not affect this adjustment
                while (effectiveLength < segmentLength && finalMainBlocksHorizontal < 100) {
                    const newMainBlocks = finalMainBlocksHorizontal + 1;
                    effectiveLength = newMainBlocks * block.main_variant.length + specialStonesLength;
                    if (effectiveLength >= segmentLength) {
                        totalBlocksHorizontal = newMainBlocks + specialStonesCount;
                        break;
                    }
                }
            }
        }
        else {
            // No end stones, just regular blocks and corner stones (if applicable)
            let specialStonesLength = 0;
            // FIRST: Account for corner stone as individual stone that takes priority
            if (isCornerSegment) {
                specialStonesLength += cornerStoneLength;
            }
            // SECOND: Calculate remaining length for main blocks after accounting for special stones
            const remainingLength = Math.max(0, segmentLength - specialStonesLength);
            const mainBlocksHorizontal = Math.ceil(remainingLength / block.main_variant.length);
            // Ensure we have at least 1 main block if the remaining length requires it
            const finalMainBlocksHorizontal = mainBlocksHorizontal === 0 && remainingLength > 0 ? 1 : mainBlocksHorizontal;
            // Count special stones for total blocks (1 per selected special stone)
            let specialStonesCount = 0;
            if (isCornerSegment)
                specialStonesCount += 1;
            // Total horizontal blocks includes main blocks + special stones
            // Always use main blocks count for the segment structure, regardless of top stone selection
            totalBlocksHorizontal = finalMainBlocksHorizontal + specialStonesCount;
            // Calculate actual effective length based on main block dimensions + special stones
            // Top stone selection should not affect the segment length
            effectiveLength = finalMainBlocksHorizontal * block.main_variant.length + specialStonesLength;
            // Ensure effective length meets minimum requirement
            if (effectiveLength < segmentLength) {
                // Add more main blocks if needed to meet segment length
                // Top stone selection should not affect this adjustment
                while (effectiveLength < segmentLength && finalMainBlocksHorizontal < 100) {
                    const newMainBlocks = finalMainBlocksHorizontal + 1;
                    effectiveLength = newMainBlocks * block.main_variant.length + specialStonesLength;
                    if (effectiveLength >= segmentLength) {
                        totalBlocksHorizontal = newMainBlocks + specialStonesCount;
                        break;
                    }
                }
            }
        }
        // Calculate total cost accounting for different stone types
        let totalCost = 0;
        if (selectedTopStone && selectedTopStone) {
            // Use top stone price for the horizontal blocks when top stones are selected
            // totalBlocksHorizontal includes top stones + special stones, so we need to calculate properly
            const mainBlockCount = totalBlocksHorizontal - (segment.startingStone ? 1 : 0) - (segment.endStone ? 1 : 0) - (isCornerSegment ? 1 : 0);
            const topStoneCost = mainBlockCount * selectedTopStone.price;
            const specialStoneCost = (segment.startingStone ? selectedEndStone.price : 0) +
                (segment.endStone ? selectedEndStone.price : 0) +
                (isCornerSegment ? block.corner_stone.price : 0);
            totalCost = totalBlocksVertical * (topStoneCost + specialStoneCost);
        }
        else {
            // Use main block price for all blocks
            totalCost = totalBlocksVertical * totalBlocksHorizontal * block.main_variant.price;
        }
        const totalBlocks = totalBlocksVertical * totalBlocksHorizontal;
        const squareMeters = (effectiveLength / 1000) * (effectiveHeight / 1000); // Convert mm to m and calculate area
        return Object.assign(Object.assign({}, segment), { heightWithMortar, 
            // Record the actual top stone height in mm when a global top stone is selected
            topStoneHeight: selectedTopStone && selectedTopStone ? selectedTopStone.thickness : 0, effectiveHeight,
            effectiveHeightWithoutMortar,
            effectiveLength,
            squareMeters, frontView: [], topView: null, preview: {
                height: Math.ceil(effectiveHeight * (zoom / 100)),
                length: Math.ceil(effectiveLength * (zoom / 100)) +
                    (selectedTopStone && selectedTopStone ? Math.ceil(selectedTopStone.length * (zoom / 100)) / 2 : blockLength / 2),
                blockHeight: blockHeight,
                blockLength: selectedTopStone && selectedTopStone ? Math.ceil(selectedTopStone.length * (zoom / 100)) : blockLength,
            }, totals: {
                horizontal: totalBlocksHorizontal,
                vertical: totalBlocksVertical,
                totalCost: totalCost,
                totalBlocks: totalBlocks,
            } });
    }));
    // Calculate blocks for each segment
    segmentDraft.forEach((segment, segmentIndex) => {
        totalBlocks += segment.totals.totalBlocks;
        // Handle the case where there are only topstones (no regular blocks)
        if (segment.totals.vertical === 0 && selectedTopStone) {
            // Use the calculated effective length for top stone preview
            const topStoneLength = selectedTopStone.length;
            const topStoneBlockLength = Math.ceil(topStoneLength * (zoom / 100));
            // Calculate how many top stone blocks fit based on effective length
            const topStoneBlocksInRow = Math.ceil(segment.effectiveLength / topStoneLength);
            const actualTopStoneBlocks = Math.max(1, topStoneBlocksInRow);
            // Render only topstones
            for (let col = 0; col < actualTopStoneBlocks; col++) {
                const blockX = col * topStoneBlockLength;
                // Determine top-corner-stone decoration for the top-only case
                const isFirstCol = col === 0;
                const isLastCol = col === actualTopStoneBlocks - 1;
                const isFirstSegment = segmentIndex === 0;
                const isLastSegment = segmentIndex === validSegments.length - 1;
                const isCornerSegment = segmentIndex < validSegments.length - 1 && segment.cornerStones > 0;
                const isTopCornerStone = Boolean(segment.topCornerStone) &&
                    ((segment.cornerStones > 0 && isCornerSegment && isLastCol) ||
                        (segment.endStone && isLastSegment && isLastCol) ||
                        (segment.startingStone && isFirstSegment && isFirstCol));
                segment.frontView.push({
                    x: blockX,
                    y: 0,
                    blockId: `${segment.id}-topstone-${col}`,
                    isCorner: false,
                    isTopStone: true,
                    isStartingStone: segment.startingStone && isFirstSegment && isFirstCol,
                    isEndingStone: segment.endStone && isLastSegment && isLastCol,
                    isTopCornerStone,
                });
            }
        }
        else {
            // Regular rendering for segments with regular blocks
            // If a top stone is selected globally, render it as a separate thin row at the very top
            const topStonePx = selectedTopStone && selectedTopStone ? Math.ceil(selectedTopStone.thickness * (zoom / 100)) : 0;
            if (selectedTopStone) {
                // Calculate how many top stone blocks fit based on effective length
                const topStoneLength = selectedTopStone.length;
                const topStoneBlockLength = Math.ceil(topStoneLength * (zoom / 100));
                // Use effective length to determine how many top stones fit
                const topStoneBlocksInRow = Math.ceil(segment.effectiveLength / topStoneLength);
                const actualTopStoneBlocks = Math.max(1, topStoneBlocksInRow);
                // Offset the top-stone row by half a block to alternate with the first regular row
                const hasRegularRows = segment.totals.vertical > 0;
                const topRowOffset = hasRegularRows ? blockLength / 2 : 0;
                for (let col = 0; col < actualTopStoneBlocks; col++) {
                    const blockX = topRowOffset + col * topStoneBlockLength;
                    // Determine top-corner-stone decoration for the top-stone row
                    const isFirstCol = col === 0;
                    const isLastCol = col === actualTopStoneBlocks - 1;
                    const isFirstSegment = segmentIndex === 0;
                    const isLastSegment = segmentIndex === validSegments.length - 1;
                    const isCornerSegment = segmentIndex < validSegments.length - 1 && segment.cornerStones > 0;
                    const isTopCornerStone = Boolean(segment.topCornerStone) &&
                        ((segment.cornerStones > 0 && isCornerSegment && isLastCol) ||
                            (segment.endStone && isLastSegment && isLastCol) ||
                            (segment.startingStone && isFirstSegment && isFirstCol));
                    segment.frontView.push({
                        x: blockX,
                        y: 0,
                        blockId: `${segment.id}-top-${col}`,
                        isCorner: false,
                        isTopStone: true,
                        isStartingStone: segment.startingStone && isFirstSegment && isFirstCol,
                        isEndingStone: segment.endStone && isLastSegment && isLastCol,
                        isTopCornerStone,
                    });
                }
            }
            // Now render the regular block rows below the (optional) top-stone row
            for (let row = 0; row < segment.totals.vertical; row++) {
                // Alternating rows: even rows start at x=0, odd rows offset by half block length
                const isAlternatingRow = row % 2 === 1;
                const rowOffset = isAlternatingRow ? blockLength / 2 : 0;
                const rowY = (selectedTopStone ? topStonePx : 0) + row * blockHeight;
                for (let col = 0; col < segment.totals.horizontal; col++) {
                    const blockX = rowOffset + col * blockLength;
                    // Determine if this is a special stone
                    const isFirstCol = col === 0;
                    const isLastCol = col === segment.totals.horizontal - 1;
                    const isFirstSegment = segmentIndex === 0;
                    const isLastSegment = segmentIndex === validSegments.length - 1;
                    const isCornerSegment = segmentIndex < validSegments.length - 1 && segment.cornerStones > 0;
                    segment.frontView.push({
                        x: blockX,
                        y: rowY,
                        blockId: `${segment.id}-${row}-${col}`,
                        isCorner: isCornerSegment && isLastCol,
                        isTopStone: false,
                        isStartingStone: segment.startingStone && isFirstSegment && isFirstCol,
                        isEndingStone: segment.endStone && isLastSegment && isLastCol,
                        isTopCornerStone: false,
                    });
                }
            }
        }
        segment.topView = {
            segmentId: segment.id,
            x: segmentIndex * 100,
            y: 50,
            width: segment.effectiveLength,
            height: Math.ceil(20 * (zoom / 100)), // Scale wall thickness with zoom level
        };
    });
    const newSegments = finishDraft(segmentDraft);
    // Aggregate corner stones from all segments
    let totalCornerStones = 0;
    let totalCornerStoneCost = 0;
    // Aggregate top stones from all segments
    let totalTopStones = 0;
    let totalTopStoneCost = 0;
    // Aggregate end stones from all segments
    let totalEndStones = 0;
    let totalEndStoneCost = 0;
    // Aggregate top corner stones from all segments
    let totalTopCornerStones = 0;
    let totalTopCornerStoneCost = 0;
    // Track selected stones (single type for all segments)
    // selectedTopStone, selectedEndStone, and selectedTopCornerStone are already declared above
    newSegments.forEach((segment, index) => {
        const withCornerStones = segment.cornerStones > 0 && block.corner_stone && index < validSegments.length - 1;
        const withTopStones = selectedTopStone && block.top_stones && block.top_stones.length > 0;
        const withStartingStones = segment.startingStone && block.end_stones && block.end_stones.length > 0 && index === 0;
        const withEndStones = segment.endStone && block.end_stones && block.end_stones.length > 0 && index === validSegments.length - 1;
        const withTopCornerStones = (segment.topCornerStone || segment.topStone) && block.corner_top;
        // Only allow corner stones for non-last segments if block supports corner stones
        if (withCornerStones) {
            // Calculate corner stones based on: quantity * wall stone total height / total vertical
            const calculatedCornerStones = segment.totals.vertical * segment.cornerStones;
            totalCornerStones += calculatedCornerStones;
            totalCornerStoneCost += calculatedCornerStones * block.corner_stone.price;
        }
        // Calculate top stones based on segment length if topStone is enabled and block supports top stones
        if (withTopStones && selectedTopStone) {
            // Calculate quantity based on topstone length, independent of main block length
            const topStonesNeeded = Math.ceil(segment.length / selectedTopStone.length);
            totalTopStones += topStonesNeeded;
            totalTopStoneCost += topStonesNeeded * selectedTopStone.price;
        }
        // Calculate starting stones based on segment height if startingStone is enabled (only for first segment)
        // and block supports end stones (uses the same endStones product)
        if (withStartingStones && selectedEndStone) {
            const startingStonesNeeded = Math.ceil(segment.heightWithMortar / block.main_variant.thickness);
            totalEndStones += startingStonesNeeded;
            totalEndStoneCost += startingStonesNeeded * selectedEndStone.price;
        }
        // Calculate end stones based on segment height if endStone is enabled (only for last segment)
        // and block supports end stones
        if (withEndStones && selectedEndStone) {
            const endStonesNeeded = Math.ceil(segment.heightWithMortar / block.main_variant.thickness);
            totalEndStones += endStonesNeeded;
            totalEndStoneCost += endStonesNeeded * selectedEndStone.price;
        }
        // Top corner stones calculation is handled after the loop to count actual corners
    });
    // Calculate top corner stones - they represent actual corners of the wall
    // Number of corners = number of segments + 1
    // Only calculate if any segment has top corner stones enabled and block supports them
    const hasAnyTopCornerStones = (block.corner_top && (selectedTopStone || newSegments.some((segment) => segment.topCornerStone))) || false;
    if (hasAnyTopCornerStones && selectedTopCornerStone) {
        totalTopCornerStones = validSegments.length + 1;
        totalTopCornerStoneCost = totalTopCornerStones * selectedTopCornerStone.variant.price;
    }
    totalBlocks -= totalCornerStones - totalEndStones; // Subtract corner stones from total blocks
    // Calculate total cost including waste, corners, and mortar
    const totalCost = totalBlocks * block.main_variant.price;
    // Calculate extra products based on total main blocks
    const extraProducts = (block.extra_products || []).map((extraProduct) => (Object.assign(Object.assign({}, extraProduct), { quantity: Math.ceil(totalBlocks * extraProduct.unit) })));
    // Add selected top stone's extra products (per 1000mm of total top stone length across all segments)
    if (selectedTopStoneWrapper && ((_b = selectedTopStoneWrapper.extraProducts) === null || _b === void 0 ? void 0 : _b.length) > 0 && totalTopStones > 0) {
        const totalTopstoneLengthMm = newSegments.reduce((sum, seg) => sum + seg.length, 0);
        const topUnits = Math.ceil(totalTopstoneLengthMm / 1000);
        selectedTopStoneWrapper.extraProducts.forEach((extra) => {
            extraProducts.push(Object.assign(Object.assign({}, extra), { quantity: Math.ceil(topUnits * extra.unit) }));
        });
    }
    // Add selected corner top stone's extra products (per corner top stone)
    if (selectedTopCornerStone && ((_c = selectedTopCornerStone.extraProducts) === null || _c === void 0 ? void 0 : _c.length) > 0 && totalTopCornerStones > 0) {
        selectedTopCornerStone.extraProducts.forEach((extra) => {
            extraProducts.push(Object.assign(Object.assign({}, extra), { quantity: Math.ceil(totalTopCornerStones * extra.unit) }));
        });
    }
    // Calculate custom extra product based on total wall area and layers
    // Formula: (Length * Height) * (Height / 0.5m) = square meters per segment
    // Static values: 0.5m per layer, 31.25 m² per product
    if ((_d = block.custom_extra_product) === null || _d === void 0 ? void 0 : _d.variant_id) {
        const totalEffectiveSquareMeters = newSegments.reduce((sum, segment) => {
            const lengthM = segment.effectiveLength / 1000; // Convert mm to m
            const heightM = segment.effectiveHeight / 1000; // Convert mm to m
            const layers = heightM / 0.5; // 0.5m per layer
            const segmentEffectiveArea = lengthM * heightM * layers;
            return sum + segmentEffectiveArea;
        }, 0);
        const productsNeeded = Math.ceil(totalEffectiveSquareMeters / 31.25);
        if (productsNeeded > 0) {
            extraProducts.push({
                variant: block.custom_extra_product,
                unit: 1,
                quantity: productsNeeded,
            });
        }
    }
    return {
        segments: newSegments,
        block,
        totalBlocks,
        cornerStones: totalCornerStones,
        topStones: totalTopStones,
        endStones: totalEndStones,
        topCornerStones: totalTopCornerStones,
        totalCost,
        cornerStoneCost: totalCornerStoneCost,
        topStoneCost: totalTopStoneCost,
        endStoneCost: totalEndStoneCost,
        topCornerStoneCost: totalTopCornerStoneCost,
        selectedTopStone,
        selectedEndStone,
        selectedTopCornerStone,
        extraProducts,
    };
};
/**
 * Generates a unique segment identifier using timestamp and random string
 * @returns Unique segment ID in format "segment-{timestamp}-{random}"
 */
export const generateSegmentId = () => {
    return `segment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
/**
 * Predefined wall block types with specifications and pricing
 *
 * Each block type includes:
 * - Basic dimensions (height × length in mm)
 * - Pricing in Swedish Krona (SEK)
 * - Weight specifications
 * - Maximum recommended height for use
 * - Compatible wall types (support, self-standing)
 * - Associated special stones (corner, top, end)
 *
 * Available block types:
 * 1. Stenbolaget 140×420 - General purpose, suitable for both wall types
 * 2. Concrete Block 200×200 - Heavy duty, support walls only
 * 3. Concrete Block 300×200 - Large format, support walls only
 * 4. Standard Brick - Traditional, self-standing walls only
 * 5. AAC Block 250×600 - Lightweight, suitable for both wall types
 */
export const defaultWallBlocks = (globalThis.metaobjects.wall_builder || []).map((raw) => {
    const toProductWithExtras = (input) => {
        if (!input)
            return null;
        return Object.assign(Object.assign({}, input), { extraProducts: input.extraProducts || [] });
    };
    const defaultTop = toProductWithExtras(raw.default_top_stone);
    const corner_top = toProductWithExtras(raw.corner_top);
    const topOptions = [defaultTop, ...(raw.top_stones || []).map(toProductWithExtras)].filter(Boolean);
    return Object.assign(Object.assign({}, raw), { default_top_stone: defaultTop, corner_top: corner_top, top_stones: topOptions });
});
const productDataItems = defaultWallBlocks
    .flatMap((block) => {
    var _a, _b, _c, _d, _e, _f, _g;
    return [
        block.main_variant,
        ...(((_a = block.top_stones) === null || _a === void 0 ? void 0 : _a.map((ts) => ts.variant)) || []),
        ...(block.end_stones || []),
        block.corner_stone,
        (_b = block.corner_top) === null || _b === void 0 ? void 0 : _b.variant,
        (_c = block.default_top_stone) === null || _c === void 0 ? void 0 : _c.variant,
        // Include extra products for main block
        ...(((_d = block.extra_products) === null || _d === void 0 ? void 0 : _d.map((extra) => extra.variant)) || []),
        // Include extra products specific to each top stone option (if any)
        ...(((_e = block.top_stones) === null || _e === void 0 ? void 0 : _e.flatMap((ts) => ts.extraProducts.map((ep) => ep.variant))) || []),
        ...(((_f = block.default_top_stone) === null || _f === void 0 ? void 0 : _f.extraProducts.map((ep) => ep.variant)) || []),
        // Include extra products for corner top stone
        ...(((_g = block.corner_top) === null || _g === void 0 ? void 0 : _g.extraProducts.map((ep) => ep.variant)) || []),
        block.custom_extra_product,
    ];
})
    .filter((item) => item !== null && item !== undefined);
LoadProductDataItems(productDataItems);
/**
 * Gets the translated wall types using the provided translation function
 * @param t - Translation function
 * @returns Array of wall types with translated names and descriptions
 */
export const getTranslatedWallTypes = (t) => [
    {
        id: 'support',
        name: t('wall_type_selection', 'support_wall_name'),
        description: t('wall_type_selection', 'support_wall_description'),
    },
    {
        id: 'self-standing',
        name: t('wall_type_selection', 'self_standing_wall_name'),
        description: t('wall_type_selection', 'self_standing_wall_description'),
    },
];
/**
 * Default wall type configurations
 *
 * Supports three main wall types:
 * - Support Wall: Retaining walls with structural support (max 3m height)
 * - Self Standing: Independent walls without external support (max 2m height)
 * - Both Types: Versatile walls suitable for multiple applications (adaptive height limits)
 */
export const formatDimensions = (height, length) => {
    return `${height}x${length}mm`;
};
/**
 * Formats dimensions for display
 * @param height - Height in millimeters
 * @param length - Length in millimeters
 * @returns Formatted dimension string (e.g., "200×400mm")
 */
// NOTE: ProductData.weight is in grams (Shopify default). Convert to kilograms for display.
import { formatNumber } from '@themes/common/utils';
export const formatWeight = (weight) => {
    if (weight == null || isNaN(weight))
        return '';
    const kg = weight / 1000; // grams -> kilograms
    const formatted = formatNumber(kg, {
        minimumFractionDigits: kg < 10 ? 1 : 0,
        maximumFractionDigits: 2,
    });
    return `${formatted} kg`;
};
export const formatVolume = (volume) => `${formatNumber(volume)} L`;
export const formatArea = (area) => `${formatNumber(area)} m²`;
export const formatPercentage = (value) => {
    return `${value}%`;
};
// Helper function to filter blocks based on wall type, height and stone requirements
export const getCompatibleBlocks = (wallType, maxSegmentHeight, segments) => {
    let filteredBlocks = defaultWallBlocks;
    // Filter by wall type compatibility
    if (wallType) {
        filteredBlocks = defaultWallBlocks.filter((block) => block.wall_types.includes(wallType.id));
    }
    // Filter by maximum segment height if provided
    if (maxSegmentHeight) {
        filteredBlocks = filteredBlocks.filter((block) => {
            // Block is compatible if it can handle the segment height
            // Check both individual block height and maxHeight limit
            return (block.main_variant.thickness > 0 &&
                block.main_variant.length > 0 &&
                block.main_variant.thickness <= maxSegmentHeight &&
                (!block.max_height || maxSegmentHeight <= block.max_height));
        });
    }
    // No longer filtering by segment stone requirements
    // We now use disabled checkboxes in the UI instead
    return filteredBlocks;
};
/**
 * Filters block types based on segment stone requirements
 * This ensures that only blocks that can support all required stone types are shown
 *
 * @param blocks - Array of wall blocks to filter
 * @param segments - Array of wall segments with their stone requirements
 * @returns All blocks without filtering, since we now use disabled checkboxes instead of filtering blocks
 */
export const filterBlocksByStoneRequirements = (blocks, segments) => {
    // Return all blocks without filtering
    return blocks;
};
/**
 * Gets the available top stone options for a given block
 * @param block - The selected wall block
 * @returns Array of available top stone options
 */
export const getAvailableTopStones = (block) => {
    return (block.top_stones || []).map((ts) => ts.variant);
};
/**
 * Gets the available end stones for a given block
 * @param block - The selected wall block
 * @returns Array of available end stones for this block
 */
export const getAvailableEndStones = (block) => {
    return block.end_stones || [];
};
/**
 * Get available top corner stone for a block
 * @param block Wall block to get top corner stone from
 * @returns The available top corner stone or null
 */
export const getAvailableTopCornerStone = (block) => {
    var _a;
    return ((_a = block.corner_top) === null || _a === void 0 ? void 0 : _a.variant) || null;
};
/**
 * Gets the selected top stone for a segment, or the default (first) option
 * @param block - The selected wall block
 * @param selectedTopStoneId - The ID of the selected top stone
 * @returns The selected top stone or the first available option
 */
export const getSelectedTopStone = (block, selectedTopStoneId) => {
    var _a;
    if (!block.top_stones || block.top_stones.length === 0)
        return null;
    if (selectedTopStoneId && selectedTopStoneId !== '') {
        const found = block.top_stones.find((stone) => stone.variant.variant_id.toString() === selectedTopStoneId);
        if (found)
            return found.variant;
    }
    // If no selection or empty selection, return default top stone if available
    return ((_a = block.default_top_stone) === null || _a === void 0 ? void 0 : _a.variant) || null;
};
/**
 * Gets the selected end stone for a segment, or the default (first) option
 * @param block - The selected wall block
 * @param selectedEndStoneId - The ID of the selected end stone
 * @returns The selected end stone or the first available option
 */
export const getSelectedEndStone = (block, selectedEndStoneId) => {
    if (!block.end_stones || block.end_stones.length === 0)
        return null;
    if (selectedEndStoneId && selectedEndStoneId !== '') {
        const found = block.end_stones.find((stone) => stone.variant_id.toString() === selectedEndStoneId);
        if (found)
            return found;
    }
    return block.end_stones[0]; // Default to first option
};
/**
 * Get selected top corner stone by ID
 * @param block Wall block containing top corner stone
 * @param selectedTopCornerStoneId ID of selected top corner stone (optional since there's only one)
 * @returns The top corner stone for this block or null if not available
 *
 * @example
 * ```typescript
 * const block = defaultWallBlocks[0];
 * const selectedTopCornerStone = getSelectedTopCornerStone(block);
 * console.log(selectedTopCornerStone?.name); // "Stenbolaget Top Corner Stone"
 * ```
 */
export const getSelectedTopCornerStone = (block, selectedTopCornerStoneId) => {
    var _a;
    return ((_a = block.corner_top) === null || _a === void 0 ? void 0 : _a.variant) || null;
};
/**
 * Default virtual scrolling configuration
 */
export const DEFAULT_VIRTUAL_SCROLL_CONFIG = {
    bufferSize: 1000,
    itemSize: 50, // Estimated 50px average block size
};
/**
 * Calculate which blocks are visible in the current viewport
 *
 * @param segments Wall segments with their block data
 * @param scrollPosition Current horizontal scroll position
 * @param containerWidth Width of the visible container
 * @param containerHeight Height of the visible container
 * @param config Virtual scrolling configuration
 * @returns Array of visible block information
 */
export const calculateVisibleBlocks = (segments, scrollPosition, containerWidth, containerHeight, config = DEFAULT_VIRTUAL_SCROLL_CONFIG) => {
    if (!segments || segments.length === 0) {
        return [];
    }
    const visibleBlocks = [];
    const viewportLeft = scrollPosition - config.bufferSize;
    const viewportRight = scrollPosition + containerWidth + config.bufferSize;
    const viewportTop = 0 - config.bufferSize;
    const viewportBottom = containerHeight + config.bufferSize;
    let currentSegmentX = 0;
    segments.forEach((segment, segmentIndex) => {
        if (!segment || !segment.frontView || !segment.preview) {
            return; // Skip invalid segments
        }
        const segmentWidth = segment.preview.length || 0;
        // Check if segment is within horizontal viewport bounds
        if (currentSegmentX + segmentWidth >= viewportLeft && currentSegmentX <= viewportRight) {
            segment.frontView.forEach((block, blockIndex) => {
                if (!block)
                    return; // Skip invalid blocks
                const blockLeft = currentSegmentX + (block.x || 0);
                const blockTop = block.y || 0;
                const blockRight = blockLeft + (segment.preview.blockLength || 50);
                const blockBottom = blockTop + (segment.preview.blockHeight || 50);
                // Check if block intersects with viewport
                const isVisible = blockRight >= viewportLeft && blockLeft <= viewportRight && blockBottom >= viewportTop && blockTop <= viewportBottom;
                if (isVisible) {
                    visibleBlocks.push({
                        segmentIndex,
                        blockIndex,
                        x: block.x || 0,
                        y: block.y || 0,
                        width: segment.preview.blockLength || 50,
                        height: segment.preview.blockHeight || 50,
                        isVisible: true,
                    });
                }
            });
        }
        currentSegmentX += segmentWidth + 10; // Add gap between segments
    });
    return visibleBlocks;
};
/**
 * Get the total number of blocks across all segments for virtualization estimates
 *
 * @param segments Wall segments
 * @returns Total block count
 */
export const getTotalBlockCount = (segments) => {
    return segments.reduce((total, segment) => total + segment.frontView.length, 0);
};
/**
 * Calculate estimated memory usage for rendering all blocks vs virtual scrolling
 *
 * @param totalBlocks Total number of blocks
 * @param visibleBlocks Number of blocks currently visible
 * @returns Memory usage comparison
 */
export const calculateMemoryUsage = (totalBlocks, visibleBlocks) => {
    const estimatedDomNodeSize = 1000; // Rough estimate of DOM node memory usage
    const totalMemoryUsage = totalBlocks * estimatedDomNodeSize;
    const virtualMemoryUsage = visibleBlocks * estimatedDomNodeSize;
    return {
        totalBlocks,
        visibleBlocks,
        totalMemoryUsage,
        virtualMemoryUsage,
        memorySavings: totalMemoryUsage - virtualMemoryUsage,
        memorySavingsPercent: totalMemoryUsage > 0 ? ((totalMemoryUsage - virtualMemoryUsage) / totalMemoryUsage) * 100 : 0,
    };
};
